import type { ApiErrorBody } from "../types/api.js";
import { getStoredToken } from "../lib/storage-token.js";
import {
  ensureFreshAccessIfNeeded,
  shouldAttemptSilentRefreshOn401,
  trySilentRefreshAndPersist,
} from "./auth-refresh.js";

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: ApiErrorBody | unknown,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

function joinBase(path: string): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? "";
  if (path.startsWith("http")) return path;
  const prefix = base.endsWith("/") ? base.slice(0, -1) : base;
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${prefix}${p}`;
}

export type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  token?: string | null;
  skipAuthRefreshRetry?: boolean;
};

/**
 * Cliente HTTP mínimo. `token`: access **JWT** de `POST /api/v1/auth/login` (Bearer).
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, token, headers: hdrs, skipAuthRefreshRetry, ...rest } = options;
  const headers = new Headers(hdrs);
  if (!headers.has("Content-Type") && body !== undefined) {
    headers.set("Content-Type", "application/json");
  }

  let bearerToken: string | null | undefined = token;
  if (bearerToken && !skipAuthRefreshRetry) {
    await ensureFreshAccessIfNeeded();
    bearerToken = getStoredToken() ?? bearerToken;
  }
  if (bearerToken) {
    headers.set("Authorization", `Bearer ${bearerToken}`);
  }

  const res = await fetch(joinBase(path), {
    ...rest,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = text;
    }
  }

  if (
    res.status === 401 &&
    bearerToken &&
    !skipAuthRefreshRetry &&
    shouldAttemptSilentRefreshOn401(path)
  ) {
    const refreshed = await trySilentRefreshAndPersist();
    if (refreshed) {
      const next = getStoredToken();
      return apiRequest<T>(path, {
        ...options,
        token: next ?? undefined,
        skipAuthRefreshRetry: true,
      });
    }
  }

  if (!res.ok) {
    const msg =
      typeof data === "object" && data !== null && "error" in data
        ? String((data as ApiErrorBody).error.message)
        : res.statusText;
    throw new ApiClientError(msg || "Error de API", res.status, data);
  }

  return data as T;
}
